import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useWp12 } from "./useWp12";
import { BrandMark } from "../App";

const NavItem = ({ to, end, children }) => (
    <NavLink
        to={to}
        end={end}
        data-testid={`v25-nav-${(typeof children === "string" ? children : "").toLowerCase().replace(/\s/g, "-")}`}
        className={({ isActive }) =>
            `px-3 py-1.5 text-[13px] rounded-md transition ${
                isActive
                    ? "bg-[hsl(var(--accent-soft))] text-[hsl(var(--accent-ink))] font-medium"
                    : "text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))] hover:bg-[hsl(var(--surface-sunken))]"
            }`
        }
    >{children}</NavLink>
);

/** Publication freshness for the active package — published week + data
 *  cutoff rendered verbatim (no client-side derivation). */
const PublicationPill = () => {
    const { data } = useWp12("home");
    if (!data?.asOfWeek) return null;
    const cutoff = (() => {
        if (!data.dataCutoff) return null;
        try {
            const d = new Date(data.dataCutoff);
            if (Number.isNaN(d.getTime())) return data.dataCutoff;
            return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
        } catch { return data.dataCutoff; }
    })();
    return (
        <span
            data-testid="v25-publication-pill"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium rounded-full border border-[hsl(var(--line))] text-[hsl(var(--ink-soft))] bg-[hsl(var(--surface-raised))] num"
        >
            <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-[hsl(var(--positive))]" />
            <span>Publication {data.asOfWeek}</span>
            {cutoff && (<><span className="text-[hsl(var(--mute))]">·</span><span>Through {cutoff}</span></>)}
        </span>
    );
};

const ShellInner = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-[hsl(var(--surface))] text-[hsl(var(--ink))] page-grain" data-testid="v25-shell">
            {/* Same header as Lodestar v1, minus the Market/Client view switch. */}
            <header className="relative z-30 border-b border-[hsl(var(--line))] bg-[hsl(var(--surface-raised))]">
                <div className="max-w-[1600px] mx-auto px-8 py-6">
                    <div className="flex items-start justify-between gap-8 flex-wrap">
                        <div className="flex items-start gap-5">
                            <BrandMark />
                            <div className="border-l border-[hsl(var(--line))] pl-5">
                                <div className="eyebrow mb-1.5">Market intelligence</div>
                                <h1 className="h-product text-[26px]">Digital Assets Lodestar</h1>
                                <p className="text-sm text-[hsl(var(--mute))] mt-1.5 max-w-xl leading-relaxed">
                                    Extracting signals from the noise.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-3">
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                                {user && (
                                    <span className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium rounded-full border border-[hsl(var(--line))] bg-[hsl(var(--surface-raised))] text-[hsl(var(--ink-soft))]" data-testid="v25-user-chip">
                                        <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))]" />
                                        <span data-testid="v25-user-display-name">{user.displayName || user.username}</span>
                                        <span className="text-[hsl(var(--mute))]">·</span>
                                        <span className="uppercase tracking-wider text-[10px]" data-testid="v25-user-role">{user.role}</span>
                                    </span>
                                )}
                                <PublicationPill />
                                {user && (
                                    <button
                                        type="button"
                                        onClick={async () => { await logout(); navigate("/login"); }}
                                        data-testid="v25-logout-button"
                                        className="text-[11px] font-medium text-[hsl(var(--mute))] hover:text-[hsl(var(--ink))] transition-colors px-2 py-1"
                                    >Sign out</button>
                                )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                                <nav className="flex items-center gap-1 overflow-x-auto max-w-full">
                                    <NavItem to="/market-v2" end>Overview</NavItem>
                                    <NavItem to="/market-v2/market">Market</NavItem>
                                    <NavItem to="/market-v2/weekly">Weekly</NavItem>
                                    <NavItem to="/market-v2/organizations">Organizations</NavItem>
                                    <NavItem to="/market-v2/evidence">Evidence</NavItem>
                                </nav>
                                <button
                                    type="button"
                                    onClick={() => navigate("/v1")}
                                    data-testid="v25-back-to-v1"
                                    className="px-2.5 py-1.5 text-[11px] rounded border border-[hsl(var(--line))] text-[hsl(var(--mute))] hover:bg-[hsl(var(--surface-sunken))] transition-colors"
                                    title="Open the previous Lodestar v1 dashboard"
                                >Lodestar v1</button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-[1600px] mx-auto px-6 py-6 relative z-10">
                <Outlet />
            </main>
        </div>
    );
};

const Shell = () => <ShellInner />;

export default Shell;
